import {
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	Divider,
	FormControlLabel,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { AwardType, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import AppImageView from '@/common/components/form-view/AppImageView';
import SingleInfoForm from './components/SingleInfoForm';
import { ExerciseTagDTO } from '@/api/DTO/common.interface';
import AppRadioView from '@/common/components/form-view/AppRadioView';
import {
	useDeleteCrossoverSingleMutation,
	useGetCrossoverSingleDetailQuery,
	useSetCrossoverSingleStatusMutation,
} from '@/api/exercise/crossoverApiSlice';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function CrossoverSingleDetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const showModal = useModal();
	const [deleteCrossoverSingle] = useDeleteCrossoverSingleMutation();
	const { crossoverId, crossoverSingleId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [setCrossoverSingleStatus] = useSetCrossoverSingleStatusMutation();

	const statusList = [
		{ id: OnoffType.off, name: t('meditation.off') },
		{ id: OnoffType.on, name: t('meditation.on') },
	];

	const introductionMediaType = [
		{ id: 3, name: t('meditation.image') },
		{ id: 2, name: t('meditation.video') },
	];

	const exerciseTypeList = [
		{ id: 1, name: t('meditation.audio') },
		{ id: 2, name: t('meditation.video') },
	];
	const canList = [
		{ id: true, name: t('meditation.can') },
		{ id: false, name: t('meditation.cannot') },
	];

	const yesOrNoList = [
		{ id: true, name: t('meditation.yes') },
		{ id: false, name: t('meditation.no') },
	];

	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];

	const { data: details } = useGetCrossoverSingleDetailQuery(
		crossoverSingleId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const transformResponse = useCallback(
		(response: ExerciseTagDTO[], type: string) => {
			if (response == null) return '';
			const arr: (string | undefined)[] = [];
			response.forEach((item) => {
				if (type == 'Zh') arr.push(item.tagNameZh);
				if (type == 'En') arr.push(item.tagNameEn);
			});
			return arr.join(',');
		},
		[]
	);

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.status == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('meditation.deleteSingle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('meditation.isDeleteSingle')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteCrossoverSingle(crossoverSingleId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteSingleSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/exercise-manage/crossover-list/info/${crossoverId}`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setCrossoverSingleStatus({
			singlePublishDTOList: [
				{
					singleId: crossoverSingleId || '',
					published: type == 1 ? true : false,
				},
			],
			id: crossoverId || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateSingleStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.singleDetail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						{details?.data.status == OnoffType.on && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(0)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOff')}
							</Button>
						)}
						{details?.data.status == OnoffType.off && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(1)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('meditation.handleOn')}
							</Button>
						)}
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				<Card
					sx={{ padding: type == FormType.view ? '0 24px' : 0, marginTop: 3 }}
				>
					<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
						<Box
							display='flex'
							justifyContent='space-between'
							sx={{ paddingTop: '24px' }}
						>
							<Typography variant='h6'>{t('meditation.record')}</Typography>
						</Box>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.exerciseStatus')}
									valueElement={
										<Chip
											color={
												details?.data.status == OnoffType.on
													? 'chipPrimary'
													: 'default'
											}
											label={showLabel(details?.data.status, statusList)}
										/>
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.createTime')}
									value={
										!details?.data.createdAt
											? '-'
											: dayjs(details?.data.createdAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppFieldView
									label={t('meditation.lastUpdateTime')}
									value={
										!details?.data.updatedAt
											? '-'
											: dayjs(details?.data.updatedAt)
													.locale('en')
													.format(DISPLAY_DATETIME_12H_FORMAT)
									}
								/>
							</Grid>
						</Grid>
					</Box>

					<Divider />
					{(() => {
						switch (type) {
							case FormType.view:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ paddingTop: '24px' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
											<Button
												endIcon={<CreateOutlinedIcon />}
												color='secondary'
												onClick={() => {
													setType(FormType.edit);
												}}
											>
												{t('button.edit')}
											</Button>
										</Box>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												<Tabs value={currentTab} onChange={handleTabChange}>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.zh')}
															</Typography>
														}
														value='zh'
													/>
													<Tab
														label={
															<Typography variant='body1'>
																{t('meditation.en')}
															</Typography>
														}
														value='en'
													/>
												</Tabs>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.exerciseTitle')}
													value={
														currentTab == 'zh'
															? details?.data.titleZh
															: currentTab == 'en'
															? details?.data.titleEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.description')}
													value={
														currentTab == 'zh'
															? details?.data.descriptionZh
															: currentTab == 'en'
															? details?.data.descriptionEn
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.exerciseTag')}
													value={
														currentTab == 'zh'
															? transformResponse(
																	details?.data.tags || [],
																	'Zh'
															  )
															: currentTab == 'en'
															? transformResponse(
																	details?.data.tags || [],
																	'En'
															  )
															: '-'
													}
												/>
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={t('meditation.second')}
													value={details?.data.mediaIntroductionTime || '-'}
												/>
											</Grid>
											<Grid item xs={6}>
												<Typography variant='body2' color='common.neutral40'>
													{t('meditation.exerciseType')}
												</Typography>
												{details?.data && (
													<AppRadioView
														value={details?.data.mediaType}
														list={exerciseTypeList}
													/>
												)}
											</Grid>
											<Grid item xs={6}>
												<AppFieldView
													label={
														details?.data.mediaType == 1
															? t('meditation.exerciseAudio')
															: t('meditation.exerciseVideo')
													}
													value={details?.data.media?.name || '-'}
													link={details?.data.media?.url}
												/>
											</Grid>
											<Grid item xs={6}>
												<Typography variant='body2' color='common.neutral40'>
													{t('meditation.isNumberTotal')}
												</Typography>
												{details?.data && (
													<AppRadioView
														value={details?.data.canCountTime}
														list={canList}
													/>
												)}
											</Grid>
											<Grid item xs={6}>
												<Typography variant='body2' color='common.neutral40'>
													{t('meditation.isNewExercise')}
												</Typography>
												{details?.data && (
													<AppRadioView
														value={details?.data.hasNew}
														list={yesOrNoList}
													/>
												)}
											</Grid>
											<Grid item xs={12}>
												<AppImageView
													label={t('meditation.exerciseCoverImage')}
													imageElement={
														details?.data.icon && (
															<img
																src={details?.data.icon.url}
																width={107}
																height={131}
																style={{
																	objectFit: 'contain',
																}}
															/>
														)
													}
												/>
											</Grid>
										</Grid>

										<Divider />
										<Typography variant='h6' sx={{ paddingTop: '24px' }}>
											{t('common.completeExercise')}
										</Typography>
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={6}>
												<Typography variant='body2' color='common.neutral40'>
													{t('meditation.isGetAward')}
												</Typography>
												{details?.data && (
													<AppRadioView
														value={details?.data.canCollectPoints}
														list={canList}
													/>
												)}
											</Grid>
											<Grid item xs={6}>
												<Typography variant='body2' color='common.neutral40'>
													{t('meditation.finishedGetAward')}
												</Typography>
												{details?.data && (
													<AppRadioView
														value={details?.data.hasReward}
														list={yesOrNoList}
													/>
												)}
											</Grid>
											{details?.data.hasReward && (
												<>
													<Grid item xs={6}>
														<AppFieldView
															label={t('taskManage.awardType')}
															value={showLabel(
																details?.data.rewardType,
																awardTypeList
															)}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('taskManage.rewardPoints')}
															value={details?.data.rewardPoints}
														/>
													</Grid>
													<Grid item xs={12} container marginBottom={3}>
														{details?.data.rewardList?.map((item) => (
															<>
																<Grid item xs={1}>
																	<AppFieldView
																		valueElement={
																			<PublishedChip
																				published={item.published}
																				publishList={PUBLISHED_LISTB}
																			/>
																		}
																	/>
																</Grid>
																<Grid item xs={1}>
																	<AppFieldView
																		label={t('regularTask.rewardItem')}
																		value={item.rewardDesc}
																	/>
																</Grid>
																<Grid item xs={10}>
																	<AppFieldView
																		label={t('regularTask.rewardItemCount')}
																		value={item.rewardNum}
																	/>
																</Grid>
															</>
														))}
													</Grid>
												</>
											)}

											<Grid item xs={6}>
												<AppFieldView
													label={t('common.finishSummary')}
													value={
														currentTab == 'zh'
															? details?.data.finishSummaryZh
															: currentTab == 'en'
															? details?.data.finishSummaryEn
															: '-'
													}
												/>
											</Grid>
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												{details?.data && (
													<FormControlLabel
														control={
															<Checkbox
																disabled
																checked={details?.data.hasIntroduction}
															/>
														}
														label={t('meditation.hasContent')}
													/>
												)}
											</Grid>
											{details?.data.hasIntroduction && (
												<>
													{currentTab == 'zh' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.introductionContent')}
																editorStateString={
																	details?.data.introductionContentZh || '-'
																}
															/>
														</Grid>
													)}
													{currentTab == 'en' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.introductionContent')}
																editorStateString={
																	details?.data.introductionContentEn || '-'
																}
															/>
														</Grid>
													)}
													<Grid item xs={6}>
														<Typography
															variant='body2'
															color='common.neutral40'
														>
															{t('meditation.introductionMediaType')}
														</Typography>
														{details?.data && (
															<AppRadioView
																value={details?.data.introductionMediaType}
																list={introductionMediaType}
															/>
														)}
													</Grid>
													{details?.data.introductionMediaType == 3 && (
														<Grid item xs={6}>
															<AppImageView
																label={t('meditation.introductionMediaImage')}
																imageElement={
																	details?.data.introductionMediaImage && (
																		<img
																			src={
																				details?.data.introductionMediaImage.url
																			}
																			width={110}
																			style={{
																				objectFit: 'contain',
																			}}
																		/>
																	)
																}
															/>
														</Grid>
													)}
													{details?.data.introductionMediaType == 2 && (
														<>
															<Grid item xs={6}>
																<Grid item xs={12}>
																	<AppFieldView
																		label={t(
																			'meditation.introductionMediaVideo'
																		)}
																		value={
																			details?.data.introductionMediaVideo
																				?.name || '-'
																		}
																		link={
																			details?.data.introductionMediaVideo
																				?.url || '-'
																		}
																	/>
																</Grid>
															</Grid>
															<Grid item xs={6}>
																<AppImageView
																	label={t('day21.previewImage')}
																	imageElement={
																		details?.data
																			.introductionMediaVideoCover && (
																			<img
																				src={
																					details?.data
																						.introductionMediaVideoCover?.url
																				}
																				width={390}
																				height={138}
																				style={{
																					objectFit: 'contain',
																				}}
																			/>
																		)
																	}
																/>
															</Grid>
														</>
													)}
												</>
											)}
										</Grid>
										<Divider />
										<Grid container spacing={3} marginTop={0} marginBottom={3}>
											<Grid item xs={12}>
												{details?.data && (
													<FormControlLabel
														control={
															<Checkbox
																disabled
																checked={details?.data.hasInformation}
															/>
														}
														label={t('meditation.hasInformation')}
													/>
												)}
											</Grid>
											{details?.data.hasInformation && (
												<>
													{currentTab == 'zh' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.informationContent')}
																editorStateString={
																	details?.data.informationContentZh || '-'
																}
															/>
														</Grid>
													)}
													{currentTab == 'en' && (
														<Grid item xs={12}>
															<RichTextDisplay
																label={t('meditation.informationContent')}
																editorStateString={
																	details?.data.informationContentEn || '-'
																}
															/>
														</Grid>
													)}
													<Grid item xs={12}>
														<Typography
															variant='body2'
															color='common.neutral40'
														>
															{t('meditation.informationImage')}
														</Typography>
														<Box
															display={'flex'}
															alignItems={'center'}
															flexWrap={'wrap'}
														>
															{(details?.data.informationMediaImages || []).map(
																(item) => (
																	<Box
																		sx={{ marginRight: '50px' }}
																		key={item.fileKey}
																	>
																		<AppImageView
																			imageElement={
																				<img
																					src={item.url}
																					width={107}
																					height={131}
																					style={{
																						objectFit: 'contain',
																					}}
																				/>
																			}
																		/>
																	</Box>
																)
															)}
														</Box>
													</Grid>
												</>
											)}
										</Grid>
									</>
								);
							case FormType.edit:
								return (
									<>
										<Box
											display='flex'
											justifyContent='space-between'
											sx={{ padding: '24px', paddingBottom: '0' }}
										>
											<Typography variant='h6'>
												{t('meditation.text')}
											</Typography>
										</Box>
										<SingleInfoForm
											type={type}
											setType={setType}
											details={details?.data}
										/>
									</>
								);
						}
					})()}
				</Card>
			</Box>
		</>
	);
}
